import { useState, useEffect } from "react";
import { apiFetch, type PaginatedResponse } from "../lib/api.ts";

export interface Project {
  id: number;
  github_id: number;
  full_name: string;
  name: string;
  owner: string;
  description: string | null;
  url: string;
  homepage: string | null;
  stars: number;
  forks: number;
  open_issues: number;
  watchers: number;
  language: string | null;
  license: string | null;
  topics: string[];
  default_branch: string | null;
  is_archived: boolean;
  is_fork: boolean;
  created_at_gh: string | null;
  updated_at_gh: string | null;
  scraped_at: string;
  logo_url: string | null;
}

interface UseProjectsOpts {
  page?: number;
  perPage?: number;
  sort?: string;
  order?: string;
  language?: string;
  category?: string;
  minStars?: number;
  q?: string;
}

export function useProjects(opts: UseProjectsOpts = {}) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(opts);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams();
    if (opts.page) params.set("page", String(opts.page));
    if (opts.perPage) params.set("per_page", String(opts.perPage));
    if (opts.sort) params.set("sort", opts.sort);
    if (opts.order) params.set("order", opts.order);
    if (opts.language) params.set("language", opts.language);
    if (opts.category) params.set("category", opts.category);
    if (opts.minStars) params.set("min_stars", String(opts.minStars));
    if (opts.q) params.set("q", opts.q);

    const qs = params.toString();
    apiFetch<PaginatedResponse<Project>>(`/projects${qs ? `?${qs}` : ""}`)
      .then((res) => {
        if (cancelled) return;
        setProjects(res.data);
        setMeta(res.meta);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [key]);

  return { projects, meta, loading, error };
}
